import { emergencyText } from "../../shared/i18n/emergencyText";
import { formatDateTime } from "../../shared/lib/format";
import type { Session } from "../../shared/session/session-context";
import type { EmergencyBypassReportRow, EmergencyOtpRequestReportRow } from "./emergencyApi";
import { clauseWords, emergencyEligible, refusalOf, refusalWords } from "./emergencyModel";

/**
 * Emergency bypasses report (batch 17 stream E7, design EMERGENCY-OTP-DESIGN-20261010 §9). Pure rules for the page: who may open
 * it, which projects it offers, the words for durations and outcomes, the date bounds sent to the server, and the CSV rows.
 */

/** Who the report is for (design §9): a Chief Pilot of at least one project, or a System Admin; never a partner, an AI agent's
 * account or test mode (the server refuses them). Advisory: the server decides and answers 403. */
export function mayOpenEmergencyReport(session: Pick<Session, "flag" | "isPartner" | "testMode" | "user" | "isSystemAdmin" | "me">): boolean {
  if (!emergencyEligible(session)) return false;
  return session.isSystemAdmin || session.me.projectRoles.some((entry) => entry.roles.includes("ProjectDirector"));
}

/**
 * D-B18-08 (UX §11.2 rule 5): the count line names what it counts — "1 bypass", "3 bypasses", "1 request", "2 requests" — and adds
 * "· more available" while another page is waiting.
 */
export function reportCountWords(tab: "bypasses" | "requests", n: number, more: boolean): string {
  const count =
    tab === "bypasses"
      ? n === 1
        ? emergencyText("reportCountBypass")
        : emergencyText("reportCountBypasses", { n })
      : n === 1
        ? emergencyText("reportCountRequest")
        : emergencyText("reportCountRequests", { n });
  return more ? emergencyText("reportMoreAvailable", { count }) : count;
}

/**
 * Item 9 (B18 DEV round OBS-B18-8): why the server refused the report (403), in words. A browser that announces automation is
 * refused as an AI assistant (EmergencyOtpRules.RequireActor), whatever the person's roles, so it is told exactly that; another
 * emergency refusal (test mode, staff only) reads as that refusal; anything else is the roles sentence.
 */
export function reportRefusalWords(error: unknown, automated: boolean): string {
  if (automated) return emergencyText("reportAutomated");
  return refusalWords(refusalOf(error)) ?? emergencyText("reportNotAllowed");
}

/** The project keys the Project filter offers: every project for a System Admin (null), else those where the caller is Chief Pilot. */
export function reportProjectKeys(session: Pick<Session, "isSystemAdmin" | "me">): string[] | null {
  if (session.isSystemAdmin) return null;
  return session.me.projectRoles.filter((entry) => entry.roles.includes("ProjectDirector")).map((entry) => entry.projectKey).sort();
}

/** "45 min", "1 h 59 min", "2 h", "3 d 4 h": whole minutes, never negative. */
export function durationWords(minutes: number): string {
  const total = Math.max(0, Math.floor(minutes));
  if (total < 60) return `${total} min`;
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  return mins > 0 ? `${hours} h ${mins} min` : `${hours} h`;
}

/** Whole minutes from `grantedAt` to now, for a grant still active (the server's `durationMinutes` is null then). */
export function activeMinutes(grantedAt: string, now: number = Date.now()): number {
  const at = Date.parse(grantedAt);
  return Number.isNaN(at) ? 0 : Math.max(0, Math.floor((now - at) / 60000));
}

/** The Duration cell: the server's figure once ended; "active for 3 h" while active (shown in red by the page). */
export function durationCell(row: Pick<EmergencyBypassReportRow, "durationMinutes" | "endedAt" | "grantedAt">, now: number = Date.now()): { text: string; active: boolean } {
  if (row.endedAt == null || row.durationMinutes == null) {
    return { text: emergencyText("reportActiveFor", { time: durationWords(activeMinutes(row.grantedAt, now)) }), active: true };
  }
  return { text: durationWords(row.durationMinutes), active: false };
}

/** "Ended because" in words. */
export function endedReasonWords(reason: EmergencyBypassReportRow["endedReason"] | undefined): string {
  switch (reason) {
    case "done":
      return emergencyText("reportEndedDone");
    case "ended_by_holder":
      return emergencyText("reportEndedHolder");
    case "revoked":
      return emergencyText("reportEndedRevoked");
    case "holder_ineligible":
      return emergencyText("reportEndedIneligible");
    default:
      return emergencyText("reportStillActive");
  }
}

/** A request's status in words; `locked` is the security signal the page shows in red. */
export function requestStatusWords(status: EmergencyOtpRequestReportRow["status"]): string {
  switch (status) {
    case "pending":
      return emergencyText("reportReqPending");
    case "expired":
      return emergencyText("reportReqExpired");
    case "locked":
      return emergencyText("reportReqLocked");
    case "superseded":
      return emergencyText("reportReqSuperseded");
    case "cancelled":
      return emergencyText("reportReqCancelled");
    default:
      return emergencyText("reportReqUsed");
  }
}

/**
 * Skipped clauses on one line: the server's details are sometimes phrases ("segregation of duties") and sometimes whole sentences
 * ("A release needs at least one scope item."); a sentence keeps its own full stop, a phrase gets a semicolon.
 */
export function joinClauses(words: readonly string[]): string {
  return words.map((word, index) => (index === words.length - 1 || /[.!?]$/.test(word) ? word : `${word};`)).join(" ");
}

/** A status code in words: the catalogue's English name, else the code humanised ("IN_PROGRESS" → "In progress"). */
export function statusWords(code: string, names: ReadonlyMap<string, string>): string {
  const named = names.get(code);
  if (named) return named;
  const plain = code.toLowerCase().replace(/_/g, " ").trim();
  return plain ? plain.charAt(0).toUpperCase() + plain.slice(1) : code;
}

/**
 * The date filters are Bangkok calendar days (the app shows every date in Bangkok time, UTC+7, no daylight saving): `from` is the
 * start of its day, `to` the last millisecond of its day, so "to 10 Oct" includes everything granted on 10 Oct. Invalid → undefined.
 */
export function dayStart(day: string | null | undefined): string | undefined {
  return isDay(day) ? `${day}T00:00:00+07:00` : undefined;
}

export function dayEnd(day: string | null | undefined): string | undefined {
  return isDay(day) ? `${day}T23:59:59.999+07:00` : undefined;
}

function isDay(day: string | null | undefined): day is string {
  return typeof day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`));
}

// ---- CSV ------------------------------------------------------------------------------------------------------------

/**
 * One CSV cell: quoted when it holds a comma, quote or line break; a value that a spreadsheet would run as a formula (=, +, -, @,
 * tab, carriage return at the start) is prefixed with an apostrophe, since reasons and summaries are free text typed by people.
 */
export function csvCell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The whole file, CRLF line ends, with a UTF-8 byte-order mark so Excel reads Thai text correctly. */
export function toCsv(rows: readonly (readonly (string | number | boolean | null | undefined)[])[]): string {
  return "\uFEFF" + rows.map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

export const BYPASS_CSV_HEADER = [
  "Grant id",
  "Project",
  "Card",
  "Summary",
  "Requester",
  "Code from",
  "Self-issued",
  "Reason",
  "Requested at",
  "Granted at",
  "Ended at",
  "Ended because",
  "Ended by",
  "Duration (min)",
  "Move at",
  "Move",
  "From",
  "To",
  "Skipped",
] as const;

/** Times in the file: ISO 8601 with the Bangkok offset ("2026-10-10T14:27:00+07:00"), readable and sortable. */
export function csvTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return "";
  return new Date(at + 7 * 3600_000).toISOString().replace(/\.\d{3}Z$/, "+07:00");
}

/**
 * The Bypasses tab as CSV (design §9: "one row per move"): each move of each grant is a row carrying its grant's columns; a grant
 * with no move is one row with the move columns empty, so no bypass is left out. Duration is empty while the bypass is active.
 */
export function bypassCsvRows(rows: readonly EmergencyBypassReportRow[], names: ReadonlyMap<string, string>): string[][] {
  const out: string[][] = [[...BYPASS_CSV_HEADER]];
  for (const row of rows) {
    const grant = [
      String(row.grantId),
      row.projectKey,
      row.item.key,
      row.item.summary,
      row.requester.displayName,
      row.issuedBy.displayName,
      row.selfIssued ? "yes" : "no",
      row.reason,
      csvTime(row.requestedAt),
      csvTime(row.grantedAt),
      csvTime(row.endedAt),
      row.endedAt ? endedReasonWords(row.endedReason) : emergencyText("reportStillActive"),
      row.endedBy?.displayName ?? "",
      row.durationMinutes == null ? "" : String(row.durationMinutes),
    ];
    if (row.moves.length === 0) {
      out.push([...grant, "", "", "", "", ""]);
      continue;
    }
    for (const move of row.moves) {
      const skipped = clauseWords(move.skipped);
      out.push([
        ...grant,
        csvTime(move.at),
        move.transitionLabel,
        statusWords(move.from, names),
        statusWords(move.to, names),
        skipped.length > 0 ? joinClauses(skipped) : emergencyText("reportNothingSkipped"),
      ]);
    }
  }
  return out;
}

export const REQUEST_CSV_HEADER = ["Request id", "Project", "Card", "Summary", "Requester", "Approvers", "Sent to", "Reason", "Status", "Wrong codes", "Requested at", "Closed at"] as const;

/** The Requests tab as CSV, one row per request. */
export function requestCsvRows(rows: readonly EmergencyOtpRequestReportRow[]): string[][] {
  return [
    [...REQUEST_CSV_HEADER],
    ...rows.map((row) => [
      String(row.requestId),
      row.projectKey,
      row.item.key,
      row.item.summary,
      row.requester.displayName,
      row.approvers.map((person) => person.displayName).join("; "),
      basisWords(row.basis),
      row.reason,
      requestStatusWords(row.status),
      String(row.failedAttempts),
      csvTime(row.requestedAt),
      csvTime(row.closedAt),
    ]),
  ];
}

/** Who the code went to, in words. */
export function basisWords(basis: EmergencyOtpRequestReportRow["basis"]): string {
  return basis === "self" ? emergencyText("selfIssued") : basis === "fallback" ? emergencyText("reportBasisFallback") : emergencyText("reportBasisPd");
}

/** "Wed 16 Sep 15:49", or "—". */
export function when(iso: string | null | undefined): string {
  return iso ? formatDateTime(iso) : "—";
}

/** The file name: `emergency-bypasses-20261010-1427.csv` (Bangkok time). */
export function csvFileName(kind: "bypasses" | "requests", now: number = Date.now()): string {
  const local = new Date(now + 7 * 3600_000).toISOString();
  const stamp = `${local.slice(0, 10).replace(/-/g, "")}-${local.slice(11, 16).replace(":", "")}`;
  return kind === "bypasses" ? `emergency-bypasses-${stamp}.csv` : `emergency-code-requests-${stamp}.csv`;
}
